import { motion, useInView, useReducedMotion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'

type Direction = 'up' | 'left' | 'right' | 'fade'

interface ScrollRevealProps {
  children: ReactNode
  direction?: Direction
  delay?: number
  duration?: number
  threshold?: number
  className?: string
}

const offsets: Record<Direction, { x: number; y: number }> = {
  up: { x: 0, y: 32 },
  left: { x: -40, y: 0 },
  right: { x: 40, y: 0 },
  fade: { x: 0, y: 0 }
}

export default function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.5,
  threshold = 0.15,
  className
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, amount: threshold })
  const reduceMotion = useReducedMotion()

  // Fail-open reveal. The IntersectionObserver behind useInView can miss its
  // initial callback for content already on-screen at load — notably on iOS
  // WebKit, and made worse by the dozens of observers a page like /stores
  // mounts at once — which left these children stuck at opacity:0 (a blank
  // page on direct/cold loads, while the navbar + footer outside the reveal
  // still showed). On mount, if the element is already within (or above) the
  // viewport, reveal it immediately so it can never stay hidden; genuinely
  // off-screen elements still animate in on scroll via useInView.
  const [forceShow, setForceShow] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const vh = window.innerHeight || document.documentElement.clientHeight || 0
    const rect = el.getBoundingClientRect()
    if (rect.top < vh && rect.bottom > 0) setForceShow(true)
  }, [])

  const offset = offsets[direction]
  const show = isInView || forceShow

  return (
    <motion.div
      ref={ref}
      className={className}
      // `initial` is deterministic (NOT branched on reduceMotion): matchMedia
      // is client-only, so reduceMotion is false during SSR and true on a
      // reduced-motion device — branching the initial prop on it produced a
      // server/client hydration mismatch that blanked the subtree. Reduced
      // motion is honoured via the zero-duration transition instead.
      initial={{ opacity: 0, x: offset.x, y: offset.y }}
      animate={show ? { opacity: 1, x: 0, y: 0 } : undefined}
      transition={reduceMotion ? { duration: 0 } : { duration, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  )
}
